import { z } from 'zod';
import { generationJobIdSchema, jobStatusSchema } from './generation.js';
/**
 * What is waiting for an admin, in one list — ADMIN-15.
 *
 * Pure functions over plain values, the shape `publish-rules.ts` argues for: the ordering
 * between buckets is the whole design, and it is what nobody exercises if reaching it
 * needs a database. `docs/decisions/admin-action-center.md` is the argument for each step
 * of it, and `deferred.md` for the four things the brief asks for that the model has no
 * columns to answer.
 */
export const attentionBucketSchema = z
    .enum([
    'ISSUES',
    'DRAFTS',
    'WARNINGS',
    'NEEDS_REVIEW',
    'READY_TO_PUBLISH',
    'PROCESSING',
])
    .describe('Which queue an item waits in. Exactly one, never two.');
/**
 * The order the queue is read in, and the order the classifier tries.
 *
 * `.options` rather than the six names again. The enum is already a tuple in declaration
 * order, and a second list is one a seventh bucket can be left out of — where `indexOf`
 * returns -1 and sorts it above every critical row.
 */
export const BUCKET_ORDER = attentionBucketSchema.options;
export const attentionPrioritySchema = z
    .enum(['CRITICAL', 'NEEDS_ATTENTION', 'NORMAL'])
    .describe('How loudly the item is asking. Sorted before the bucket.');
export const PRIORITY_ORDER = attentionPrioritySchema.options;
const plural = (n, one) => `${String(n)} ${one}${n === 1 ? '' : 's'}`;
/**
 * Which queue a lesson waits in, or nothing if it waits in none.
 *
 * First match wins, and the order is the argument — one bucket per item, so the counts on
 * the cards and the rows under them cannot disagree. Why each step sits where it does:
 * `docs/decisions/admin-action-center.md`.
 *
 * `PUBLISHED` and `ARCHIVED` want nothing. This queue is what is unfinished, and a screen
 * that also lists what is finished is the dashboard the brief asks it not to become.
 */
export function lessonAttention(lesson) {
    if (!lesson.draft)
        return undefined;
    if (lesson.blockingFindings > 0) {
        return {
            bucket: 'ISSUES',
            priority: 'CRITICAL',
            reason: `${plural(lesson.blockingFindings, 'blocking finding')} nobody has resolved.`,
        };
    }
    if (lesson.blockers > 0) {
        return {
            bucket: 'DRAFTS',
            priority: 'NORMAL',
            reason: `Incomplete — ${plural(lesson.blockers, 'publish rule')} still unmet.`,
        };
    }
    if (lesson.warningFindings > 0) {
        return {
            bucket: 'WARNINGS',
            priority: 'NEEDS_ATTENTION',
            reason: `${plural(lesson.warningFindings, 'warning')} from the reviewer, none of them blocking.`,
        };
    }
    if (!lesson.handEdited) {
        return {
            bucket: 'NEEDS_REVIEW',
            priority: 'NEEDS_ATTENTION',
            reason: 'Generated, and no human has read it yet.',
        };
    }
    return {
        bucket: 'READY_TO_PUBLISH',
        priority: 'NORMAL',
        reason: 'Reviewed, and the publish gate is clean.',
    };
}
/**
 * The run statuses that want something, as a query can ask for them.
 *
 * Beside the `switch` below rather than in the service, because it is the same fact: the
 * two arms returning `undefined` are `SUCCEEDED` and `CANCELLED`, and a service holding
 * its own copy keeps filtering the old set when a status is added — where the `switch`
 * refuses to compile.
 */
export const LIVE_JOB_STATUSES = jobStatusSchema.options.filter((status) => jobAttention({ status, rowsTotal: 0, rowsSucceeded: 0 }) !== undefined);
/**
 * Which queue a run waits in, or nothing.
 *
 * `PARTIAL` is an issue and not a success, which is the whole reason that status exists:
 * a job reporting success with four of five rows failed is what GEN-15 was written for.
 * `CANCELLED` wants nothing, because somebody already decided.
 */
export function jobAttention(job) {
    switch (job.status) {
        case 'QUEUED':
        case 'RUNNING':
        case 'REVIEWING':
            return {
                bucket: 'PROCESSING',
                priority: 'NORMAL',
                reason: `Running — ${String(job.rowsSucceeded)} of ${String(job.rowsTotal)} rows done.`,
            };
        case 'FAILED':
            return {
                bucket: 'ISSUES',
                priority: 'CRITICAL',
                reason: job.error ?? 'Generation failed, with no message.',
            };
        case 'PARTIAL':
            return {
                bucket: 'ISSUES',
                priority: 'CRITICAL',
                reason: `${plural(job.rowsTotal - job.rowsSucceeded, 'row')} of ${String(job.rowsTotal)} failed.`,
            };
        case 'SUCCEEDED':
        case 'CANCELLED':
            return undefined;
    }
}
export const attentionTargetSchema = z
    .discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('LESSON'), id: z.string().min(1) }),
    z.strictObject({ kind: z.literal('JOB'), id: generationJobIdSchema }),
])
    .describe('What the item is about — a lesson, or the run that was writing it.');
export const attentionItemSchema = z
    .strictObject({
    target: attentionTargetSchema,
    bucket: attentionBucketSchema,
    priority: attentionPrioritySchema,
    title: z.string().min(1),
    /** Where it sits: `Marketing → Pricing` for a lesson, the planner for a run. */
    context: z.string(),
    reason: z.string().min(1),
    updatedAt: z.iso.datetime(),
})
    .describe('One thing waiting for a decision.');
export const attentionSummarySchema = z
    .strictObject({
    ISSUES: z.number().int().min(0),
    DRAFTS: z.number().int().min(0),
    WARNINGS: z.number().int().min(0),
    NEEDS_REVIEW: z.number().int().min(0),
    READY_TO_PUBLISH: z.number().int().min(0),
    PROCESSING: z.number().int().min(0),
})
    .describe('How many are in each bucket, counted before the filter so the cards do not move when a chip is picked.');
/**
 * The last run that finished, whatever it finished as — the brief's "connect it directly
 * to Bulk Upload". Absent until one has.
 */
export const lastImportSchema = z
    .strictObject({
    jobId: generationJobIdSchema,
    status: jobStatusSchema,
    finishedAt: z.iso.datetime(),
    rowsTotal: z.number().int().min(0),
    rowsSucceeded: z.number().int().min(0),
    lessonsCreated: z.number().int().min(0),
})
    .describe('What last night produced.');
export const browseAttentionSchema = z
    .strictObject({
    bucket: attentionBucketSchema.optional(),
    jobId: generationJobIdSchema
        .optional()
        .describe('Only what this run produced, and the run itself.'),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    offset: z.coerce.number().int().min(0).default(0),
})
    .describe('GET /authoring/attention');
export const attentionQueueSchema = z
    .strictObject({
    summary: attentionSummarySchema,
    items: z.array(attentionItemSchema),
    /** How many matched the filter, so the screen can say what it is showing part of. */
    total: z.number().int().min(0),
    lastImport: lastImportSchema.optional(),
})
    .describe('The Action Center, in one response.');
/**
 * The queue's order: how loudly first, then which bucket, then most recently touched.
 *
 * Priority outranks bucket because that is what the brief asks for — blocking errors
 * before cosmetic ones — and the two are not the same axis: `PROCESSING` is last by
 * bucket, but a failed run that started there is `ISSUES`.
 */
export function compareAttention(a, b) {
    const byPriority = PRIORITY_ORDER.indexOf(a.priority) - PRIORITY_ORDER.indexOf(b.priority);
    if (byPriority !== 0)
        return byPriority;
    const byBucket = BUCKET_ORDER.indexOf(a.bucket) - BUCKET_ORDER.indexOf(b.bucket);
    if (byBucket !== 0)
        return byBucket;
    return b.updatedAt.localeCompare(a.updatedAt);
}
/**
 * An empty count for every bucket, so a caller adds rather than initialises.
 *
 * Derived from the enum rather than written out, which is the third copy of the six names
 * this file used to carry. `attentionSummarySchema` keeps its literal keys: those are the
 * response's shape in `openapi.json`, not a list to maintain.
 */
export function emptySummary() {
    return attentionSummarySchema.parse(Object.fromEntries(BUCKET_ORDER.map((bucket) => [bucket, 0])));
}
/** Whether the admin is caught up — the empty state's one question. */
export const caughtUp = (summary) => BUCKET_ORDER.every((bucket) => summary[bucket] === 0);
//# sourceMappingURL=attention.js.map